import { apiService } from '../services/api';

export interface NotificationData {
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error' | 'update';
  data?: any;
}

/**
 * Notification Service
 * Creates notifications for various events in the application
 */
class NotificationService {
  /**
   * Get user ID from token or localStorage
   */
  private getUserId(): string | null {
    try {
      // Try to get user from localStorage first
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user && user.id) {
          return String(user.id);
        }
      }

      // Fallback: Try to get user ID from token
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return payload.user_id || payload.sub || payload.id || null;
        } catch (e) {
          console.warn('Could not decode token for notification:', e);
        }
      }
    } catch (error) {
      console.warn('Error getting user ID for notification:', error);
    }
    return null;
  }

  /**
   * Create a notification
   * Tries to save via API if user is authenticated, otherwise saves to localStorage
   */
  async createNotification(message: string, type: NotificationData['type'] = 'info', data?: any, userId?: string): Promise<void> {
    try {
      // Use provided userId or try to get it
      const userIdToUse = userId || this.getUserId();

      // Try API first if we have a user ID
      if (userIdToUse) {
        try {
          console.log('🔔 NotificationService: Creating notification via API for user:', userIdToUse);
          const response = await apiService.createNotification(userIdToUse, { message, type, data });
          if (response && response.success) {
            console.log('✅ NotificationService: Notification created successfully in database');
            return;
          }
        } catch (error: any) {
          console.warn('⚠️ NotificationService: Failed to create notification via API:', error?.message || error);
          // Continue to localStorage fallback
        }
      } else {
        console.warn('⚠️ NotificationService: No user ID available, saving to localStorage only');
      }

      // Fallback to localStorage
      const saved = localStorage.getItem('notifications') || '[]';
      const notifications = JSON.parse(saved);
      const newNotification = {
        id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        message,
        type: type || 'info',
        date: new Date().toISOString(),
        read: false,
        ...(data || {})
      };
      notifications.unshift(newNotification); // Add to beginning
      localStorage.setItem('notifications', JSON.stringify(notifications));
      
      console.log('✅ NotificationService: Notification saved to localStorage');
      
      // Dispatch event to update notification count
      window.dispatchEvent(new CustomEvent('notificationCreated'));
    } catch (error) {
      console.error('❌ NotificationService: Error creating notification:', error);
      // Don't throw - notification creation should not break the flow
    }
  }

  /**
   * Create notification for order placed
   */
  async notifyOrderPlaced(orderId: string, orderNumber: string, totalAmount: number, shopCount: number = 1, userId?: string): Promise<void> {
    const message = shopCount > 1
      ? `🎉 ${shopCount} orders placed successfully! Order #${orderNumber} - Total: ₹${totalAmount.toFixed(2)}`
      : `🎉 Order placed successfully! Order #${orderNumber} - Total: ₹${totalAmount.toFixed(2)}`;
    
    await this.createNotification(message, 'success', {
      orderId,
      orderNumber,
      totalAmount,
      shopCount
    }, userId);
  }

  /**
   * Create notification for order status change
   */
  async notifyOrderStatusChange(orderId: string, orderNumber: string, oldStatus: string, newStatus: string, userId?: string): Promise<void> {
    const statusMessages: { [key: string]: string } = {
      'pending': '⏳',
      'confirmed': '✅',
      'processing': '⚙️',
      'shipped': '🚚',
      'delivered': '📦',
      'cancelled': '❌'
    };

    const emoji = statusMessages[newStatus] || '📋';
    const message = `${emoji} Order #${orderNumber} status updated: ${oldStatus} → ${newStatus}`;
    
    await this.createNotification(message, 'update', {
      orderId,
      orderNumber,
      oldStatus,
      newStatus
    }, userId);
  }

  /**
   * Create notification for order shipped
   */
  async notifyOrderShipped(orderId: string, orderNumber: string, trackingNumber?: string, userId?: string): Promise<void> {
    const message = trackingNumber
      ? `🚚 Order #${orderNumber} has been shipped! Tracking: ${trackingNumber}`
      : `🚚 Order #${orderNumber} has been shipped!`;
    
    await this.createNotification(message, 'success', {
      orderId,
      orderNumber,
      trackingNumber
    }, userId);
  }

  /**
   * Create notification for order delivered
   */
  async notifyOrderDelivered(orderId: string, orderNumber: string, userId?: string): Promise<void> {
    const message = `📦 Order #${orderNumber} has been delivered! Thank you for shopping with us!`;
    
    await this.createNotification(message, 'success', {
      orderId,
      orderNumber
    }, userId);
  }

  /**
   * Create notification for payment received
   */
  async notifyPaymentReceived(orderId: string, orderNumber: string, amount: number, paymentMethod: string, userId?: string): Promise<void> {
    const message = `💳 Payment of ₹${amount.toFixed(2)} received for Order #${orderNumber} via ${paymentMethod}`;
    
    await this.createNotification(message, 'success', {
      orderId,
      orderNumber,
      amount,
      paymentMethod
    }, userId);
  }

  /**
   * Create notification for order cancelled
   */
  async notifyOrderCancelled(orderId: string, orderNumber: string, reason?: string, userId?: string): Promise<void> {
    const message = reason
      ? `❌ Order #${orderNumber} has been cancelled. Reason: ${reason}`
      : `❌ Order #${orderNumber} has been cancelled`;
    
    await this.createNotification(message, 'error', {
      orderId,
      orderNumber,
      reason
    }, userId);
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;
